import { doc, getDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "./firebase";

type State = "ok" | "pending" | "bad";

/**
 * Probes that Firestore is reachable AND that the rules are actually loaded.
 *
 * The read is EXPECTED to be denied: this asks for a location the user is not
 * a member of. A `permission-denied` proves both facts at once, which a
 * successful read would not — an emulator started without rules would happily
 * return nothing and look healthy.
 *
 * The probe id must not match Firestore's reserved `__*__` pattern; that is
 * rejected as malformed before the rules are ever consulted.
 */
export function useRulesProbe(): { state: State; detail: string } {
  const [probe, setProbe] = useState<{ state: State; detail: string }>({
    state: "pending",
    detail: "connecting…",
  });

  useEffect(() => {
    getDoc(doc(db, "locations", "connectivity-probe"))
      .then(() => setProbe({ state: "bad", detail: "reachable, RULES NOT ENFORCED" }))
      .catch((e: { code?: string; message: string }) => {
        if (e.code === "permission-denied") {
          setProbe({ state: "ok", detail: "reachable, rules enforced" });
        } else {
          setProbe({ state: "bad", detail: shorten(e.message) });
        }
      });
  }, []);

  return probe;
}

const shorten = (m: string) => m.replace(/^Firebase:\s*/, "").slice(0, 48);
